import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Upload,
  Download,
  ArrowLeft,
  Loader2,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Info
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { useAuth } from '@/context/AuthContext'
import { USER_ROLES } from '@/utils/constants'
import toast from 'react-hot-toast'
import { getEntityErrorMessage, getUserFriendlyErrorMessage } from '@/utils/errorMessages'

const BulkUpload = () => {
  const { dataInputId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadResult, setUploadResult] = useState(null)
  const [dragActive, setDragActive] = useState(false)

  // Redirect User role away from bulk upload page (only User role is restricted)
  useEffect(() => {
    if (user && user.role === USER_ROLES.USER) {
      toast.error('You do not have permission to access bulk upload')
      navigate(`/data-inputs/${dataInputId}?tab=records`)
    }
  }, [user, dataInputId, navigate])

  const {
    data: dataInput,
    isLoading: dataInputLoading
  } = useQuery(
    ['dataInput', dataInputId],
    () => dataInputsService.getById(dataInputId),
    {
      onError: (error) => {
        console.error('Error loading data input:', error)
      }
    }
  )

  // Get only active attributes for bulk upload
  const {
    data: activeAttributes,
    isLoading: attributesLoading
  } = useQuery(
    ['activeAttributes', dataInputId],
    () => dataInputsService.getActiveAttributes(dataInputId),
    {
      enabled: !!dataInputId,
      onError: (error) => {
        console.error('Error loading attributes:', error)
      }
    }
  )

  // Check if data input has file attributes
  const hasFileAttributes = activeAttributes?.some(attr =>
    ['File', 'Image', 'Video', 'Audio', 'Document', 'Archive'].includes(attr.data_type)
  )

  // Bulk upload mutation
  const bulkUploadMutation = useMutation(
    (file) => dataInputsService.bulkUploadRecords(dataInputId, file),
    {
      onSuccess: (result) => {
        setUploadResult(result)
        if (result.successful_records > 0) {
          toast.success(`Successfully uploaded ${result.successful_records} records!`)
          // Remove all records queries from cache and force fresh fetch
          queryClient.removeQueries({
            predicate: (query) => {
              return query.queryKey[0] === 'records' && query.queryKey[1] === dataInputId
            }
          })
          queryClient.invalidateQueries({ queryKey: ['dataInput', dataInputId] })
        }
        if (result.failed_records > 0) {
          toast.error(`${result.failed_records} records failed to upload. Check details below.`)
        }
      },
      onError: (error) => {
        console.error('Error uploading file:', error)

        // Check if it's a validation error (422 status)
        if (error.response?.status === 422 && error.response?.data?.detail) {
          const errorData = error.response.data.detail
          setUploadResult(errorData)

          // Create user-friendly error message
          const errorCount = errorData.failed_records || 0
          const totalRows = errorData.total_rows || 0

          let errorMessage = `Unable to upload: Found ${errorCount} error${errorCount !== 1 ? 's' : ''} in ${totalRows} row${totalRows !== 1 ? 's' : ''}. `

          // Add specific error details if available
          if (errorData.errors && errorData.errors.length > 0) {
            const firstError = errorData.errors[0]
            if (errorData.errors.length === 1) {
              errorMessage += `Row ${firstError.row}: ${getUserFriendlyErrorMessage(firstError.error, firstError.error)}`
            } else {
              errorMessage += `Row ${firstError.row}: ${getUserFriendlyErrorMessage(firstError.error, firstError.error)}. See details below for all errors.`
            }
          } else {
            errorMessage += 'Please check your data and try again.'
          }

          toast.error(errorMessage, { duration: 6000 }) // Show longer for detailed message
        } else {
          const errorMessage = getEntityErrorMessage('record', 'bulkUpload', error)
          toast.error(errorMessage)
        }
      }
    }
  )

  const handleFileSelect = (file) => {
    if (file && file.type === 'text/csv') {
      setSelectedFile(file)
      setUploadResult(null)
    } else {
      toast.error('Please select a valid CSV file')
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragActive(false)
  }

  const handleUpload = () => {
    if (selectedFile) {
      bulkUploadMutation.mutate(selectedFile)
    }
  }

  const generateTemplate = () => {
    if (!activeAttributes || activeAttributes.length === 0) {
      toast.error('No active attributes defined for this data input')
      return
    }

    // Create CSV template with active attribute names as headers
    const headers = activeAttributes.map(attr => attr.name)
    let csvContent = headers.join(',') + '\n'

    // Add example row with data type hints
    const exampleRow = activeAttributes.map(attr => {
      switch (attr.data_type) {
        case 'Number': return '1,234'
        case 'Decimal': return '1,234.56'
        case 'Boolean': return 'true'
        case 'Date': return '2024-01-01'
        case 'Datetime': return '2024-01-01T10:00:00'
        case 'Email': return 'example@email.com'
        case 'URL': return 'https://example.com'
        default: return 'Sample text'
      }
    })
    csvContent += exampleRow.join(',')

    // Download the template
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${dataInput.name}_template.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    toast.success('Template downloaded successfully!')
  }

  if (dataInputLoading || attributesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading data input details...</span>
        </div>
      </div>
    )
  }

  if (!dataInput) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <FileText className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Data input not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The data input you're trying to upload to doesn't exist.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/data-inputs')}
              className="btn-primary btn-sm"
            >
              Back to Data Inputs
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/data-inputs/${dataInputId}?tab=records`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Records
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bulk Upload Records</h1>
            <p className="mt-1 text-sm text-gray-600">
              Upload multiple records to {dataInput.name} using a CSV file
            </p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">CSV Format Requirements</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>First row must contain column headers</li>
                <li>Column headers should match attribute names exactly</li>
                <li>Data will be validated according to attribute definitions</li>
                <li>Boolean values: use "true"/"false", "1"/"0", or "yes"/"no"</li>
                <li>Required fields cannot be empty</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Template Download */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Download Template</h3>
          <p className="mt-1 text-sm text-gray-600">
            Download a CSV template with the correct column headers for this data input
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">CSV Template</h4>
              <p className="text-sm text-gray-500">
                Includes all {activeAttributes?.length || 0} active attribute columns with example data
              </p>
            </div>
            <button
              onClick={generateTemplate}
              className="btn-outline btn-sm flex items-center"
              disabled={!dataInput.attributes || dataInput.attributes.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </button>
          </div>
        </div>
      </div>

      {/* File Attributes Warning */}
      {hasFileAttributes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Bulk Upload Not Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This data input contains file attributes (File, Image, Video, Audio, Document, or Archive).
                  Bulk upload is not supported for data inputs with file attributes because files cannot be
                  uploaded through CSV.
                </p>
                <p className="mt-2">
                  <strong>Alternative:</strong> Use the single record creation form to upload files individually.
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate(`/data-inputs/${dataInputId}/records/create`)}
                  className="btn-primary btn-sm"
                >
                  Create Single Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Upload */}
      {!hasFileAttributes && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Upload CSV File</h3>
            <p className="mt-1 text-sm text-gray-600">
              Select or drag and drop your CSV file to upload records
            </p>
          </div>
        <div className="p-6">
          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-primary-400 bg-primary-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="text-primary-600 hover:text-primary-500 font-medium">
                  Click to upload
                </span>
                <span className="text-gray-500"> or drag and drop</span>
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">CSV files only</p>
          </div>

          {/* Selected File */}
          {selectedFile && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {selectedFile && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleUpload}
                disabled={bulkUploadMutation.isLoading}
                className="btn-primary btn-md flex items-center"
              >
                {bulkUploadMutation.isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Records
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Upload Results */}
      {uploadResult && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Upload Results</h3>
          </div>
          <div className="p-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-blue-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Total Rows</p>
                    <p className="text-lg font-bold text-blue-600">{uploadResult.total_rows}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Successful</p>
                    <p className="text-lg font-bold text-green-600">{uploadResult.successful_records}</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Failed</p>
                    <p className="text-lg font-bold text-red-600">{uploadResult.failed_records}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Message */}
            <div className="mb-6">
              {uploadResult.failed_records > 0 ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-red-900 mb-1">Upload Failed</h4>
                      <p className="text-sm text-red-800">
                        {uploadResult.failed_records === 1
                          ? `1 validation error was found in your data.`
                          : `${uploadResult.failed_records} validation errors were found in your data.`
                        } No records were uploaded to maintain data integrity.
                      </p>
                      <p className="text-sm text-red-700 mt-2">
                        Please fix the errors shown below and try uploading again.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-green-900 mb-1">Upload Successful</h4>
                      <p className="text-sm text-green-800">
                        All {uploadResult.successful_records} record{uploadResult.successful_records !== 1 ? 's' : ''}
                        {uploadResult.successful_records === 1 ? ' was' : ' were'} successfully uploaded and validated.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Errors */}
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Validation Errors ({uploadResult.errors.length})
                </h4>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h5 className="text-sm font-medium text-blue-900">How to fix these errors:</h5>
                      <div className="text-sm text-blue-800 mt-1">
                        <p>1. Download your original file or create a new one</p>
                        <p>2. Fix the errors shown below (check row numbers and values)</p>
                        <p>3. Save the file and upload it again</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <div className="space-y-3">
                    {uploadResult.errors.map((error, index) => {
                      // Categorize error types for better user guidance
                      const isPhoneError = error.error?.toLowerCase().includes('phone')
                      const isDecimalError = error.error?.toLowerCase().includes('decimal') || error.error?.toLowerCase().includes('number')
                      const isRequiredError = error.error?.toLowerCase().includes('required')

                      return (
                        <div key={index} className="bg-white border border-red-300 rounded-md p-3">
                          <div className="flex items-start">
                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-red-800 font-medium">
                                Row {error.row}: {error.error}
                              </p>
                              {error.column && (
                                <div className="text-xs text-red-600 mt-2 space-y-1">
                                  <p>
                                    <span className="font-medium">Column:</span> {error.column}
                                    {error.value && (
                                      <span className="ml-2">
                                        <span className="font-medium">Current value:</span>
                                        <span className="font-mono bg-red-100 px-1 ml-1 rounded">"{error.value}"</span>
                                      </span>
                                    )}
                                  </p>
                                  {/* Add helpful suggestions based on error type */}
                                  {isPhoneError && (
                                    <p className="text-red-700 italic">💡 Tip: Phone numbers should be 8+ digits (e.g., 09123456789)</p>
                                  )}
                                  {isDecimalError && (
                                    <p className="text-red-700 italic">💡 Tip: Use numbers only (e.g., 1000 or 1000.50)</p>
                                  )}
                                  {isRequiredError && (
                                    <p className="text-red-700 italic">💡 Tip: This field cannot be empty</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Success Records */}
            {uploadResult.created_records && uploadResult.created_records.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Successfully Created Records ({uploadResult.created_records.length})
                </h4>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <div className="space-y-2">
                    {uploadResult.created_records.slice(0, 10).map((record, index) => (
                      <div key={index} className="text-sm text-green-800">
                        <span className="font-medium">Row {record.row}:</span>
                        <span className="ml-2">Record created successfully</span>
                      </div>
                    ))}
                    {uploadResult.created_records.length > 10 && (
                      <div className="text-sm text-green-600 italic">
                        ... and {uploadResult.created_records.length - 10} more records
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setUploadResult(null)
                  setSelectedFile(null)
                }}
                className="btn-outline btn-md"
              >
                Upload Another File
              </button>
              <button
                onClick={() => navigate(`/data-inputs/${dataInputId}?tab=records`)}
                className="btn-primary btn-md"
              >
                View All Records
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BulkUpload
